import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import ReactSlider from "react-slider";
import useFetchApiData from "../helper/useFetchApiData";
import axios from "axios";
import { Empty } from "antd";
import { addToCart } from "../helper/cartfunction";

const ShopSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract query params
  const searchQuery = searchParams.get("search") || "";
  const categoryId = searchParams.get("cat_id") || null;
  const subCategoryId = searchParams.get("sub_cat_id") || null;

  // State management
  const [data, setData] = useState({
    products: [],
    lowest_price: 1,
    highest_price: 100000,
  });
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [priceRange, setPriceRange] = useState([1, 1000]);
  const [subCategories, setSubCategories] = useState([]);
  const [grid, setGrid] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [sortBy, setSortBy] = useState("popular"); // Default sorting

  // Fetch categories
  const categories = useFetchApiData("/categories");

  // Update URL when filters change
  const updateURL = (cat_id = null, sub_cat_id = null, search = "") => {
    const params = new URLSearchParams();
    if (cat_id) params.set("cat_id", cat_id);
    if (sub_cat_id) params.set("sub_cat_id", sub_cat_id);
    if (search) params.set("search", search);

    navigate(
      `/shop${cat_id ? `/${cat_id}` : ""}${
        sub_cat_id ? `/${sub_cat_id}` : ""
      }?${params.toString()}`
    );
  };

  // Fetch products based on search params
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URI}/products/search?name=${searchQuery}`
        );
        const fetchedData = response.data;

        setData(fetchedData);
        setProducts(fetchedData.products || []);
        setFilteredProducts(fetchedData.products || []);

        if (fetchedData.lowest_price && fetchedData.highest_price) {
          setPriceRange([fetchedData.lowest_price, fetchedData.highest_price]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setData({ products: [], lowest_price: 1, highest_price: 100000 });
        setProducts([]);
        setFilteredProducts([]);
      }
    };

    fetchProducts();
  }, [searchQuery]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (categoryId) {
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_URI}/categories/childes/${categoryId}`
        )
        .then((response) => setSubCategories(response.data))
        .catch((error) => {
          console.error("Error fetching subcategories:", error);
          setSubCategories([]);
        });
    } else {
      setSubCategories([]);
    }
  }, [categoryId]);

  // Apply filters
  useEffect(() => {
    let filtered = products;

    if (categoryId) {
      filtered = filtered.filter((product) =>
        product.category_ids.some((cat) => cat.id == categoryId)
      );
    }

    if (subCategoryId) {
      filtered = filtered.filter((product) =>
        product.category_ids.some((cat) => cat.id == subCategoryId)
      );
    }

    filtered = filtered.filter(
      (product) =>
        product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    setFilteredProducts(filtered);
  }, [products, categoryId, subCategoryId, priceRange]);

  // Sort products
  const sortedProducts = useMemo(() => {
    let sorted = [...filteredProducts];

    switch (sortBy) {
      case "price_low_to_high":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_high_to_low":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "name_a_to_z":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name_z_to_a":
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // Default sorting (popular or no sorting)
        break;
    }

    return sorted;
  }, [filteredProducts, sortBy]);

  // Handle sorting change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  // Sidebar controller
  const sidebarController = () => {
    setActive(!active);
    setSidebarOpen(!sidebarOpen);
  };

  // Handle category filter change
  const onCategoryFilter = (cat_id) => {
    updateURL(cat_id, null, searchQuery);
  };

  // Handle subcategory filter change
  const onsubcatchange = (sub_cat_id) => {
    updateURL(categoryId, sub_cat_id, searchQuery);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const search = e.target.value;
    updateURL(categoryId, subCategoryId, search);
  };

  return (
    <section className="shop py-80">
      <div className={`side-overlay ${active && "show"}`}></div>
      <div className="container container-lg">
        <div className="row">
          {/* Sidebar Start */}
          <div className="col-lg-4">
            <div className={`shop-sidebar ${active && "active"}`}>
              <button
                onClick={sidebarController}
                type="button"
                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
              >
                <i className="ph ph-x" />
              </button>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Product Category
                </h6>
                <ul className="max-h-540 overflow-y-auto scroll-sm">
                  {categories.map((item) => (
                    <li key={item.id} className="mb-24">
                      <button
                        onClick={() => onCategoryFilter(item.id)}
                        className="text-gray-900 hover-text-main-600"
                      >
                        {item.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              {subCategories.length > 0 && (
                <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                  <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                    Sub Categories
                  </h6>
                  <ul className="max-h-540 overflow-y-auto scroll-sm">
                    {subCategories.map((item) => (
                      <li key={item.id} className="mb-24">
                        <button
                          onClick={() => onsubcatchange(item.id)}
                          className="text-gray-900 hover-text-main-600"
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Filter by Price
                </h6>
                <div className="custom--range">
                  <ReactSlider
                    className="horizontal-slider"
                    thumbClassName="example-thumb"
                    trackClassName="example-track"
                    value={priceRange}
                    onChange={(newRange) => setPriceRange(newRange)}
                    ariaLabel={["Lower thumb", "Upper thumb"]}
                    ariaValuetext={(state) =>
                      `Thumb value ${
                        state.valueNow > 8000
                          ? (state.valueNow / 1000).toFixed(1) + "K"
                          : state.valueNow
                      }`
                    }
                    renderThumb={(props, state) => (
                      <div {...props} key={state.index}>
                        {state.valueNow > 8000
                          ? `${(state.valueNow / 1000).toFixed(1)}K`
                          : state.valueNow}
                      </div>
                    )}
                    pearling
                    minDistance={10}
                    min={data.lowest_price}
                    max={data.highest_price}
                  />
                  <br />
                  <br />
                  <div className="flex-between flex-wrap-reverse gap-8 mt-24 ">
                    <button
                      type="button"
                      className="btn btn-main h-40 flex-align"
                      onClick={() => sidebarController()}
                    >
                      Filter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sidebar End */}

          {/* Content Start */}
          <div className="col-lg-8">
            {/* Top Start */}
            <div className="flex-between gap-16 flex-wrap mb-40 ">
              <span className="text-gray-900">
                Showing {sortedProducts.length} result
              </span>
              <div className="position-relative flex-align gap-16 flex-wrap">
                <div className="list-grid-btns flex-align gap-16">
                  <button
                    onClick={() => setGrid(true)}
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                      grid && "border-main-600 text-white bg-main-600"
                    }`}
                  >
                    <i className="ph-bold ph-list-dashes" />
                  </button>
                  <button
                    onClick={() => setGrid(false)}
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${
                      !grid && "border-main-600 text-white bg-main-600"
                    }`}
                  >
                    <i className="ph ph-squares-four" />
                  </button>
                </div>
                <div className="position-relative text-gray-500 flex-align gap-4 text-14">
                  <label
                    htmlFor="sorting"
                    className="text-inherit flex-shrink-0"
                  >
                    Sort by:{" "}
                  </label>
                  <select
                    value={sortBy}
                    onChange={handleSortChange}
                    className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
                    id="sorting"
                  >
                    <option value="popular">Popular</option>
                    <option value="price_low_to_high">
                      Price: Low to High
                    </option>
                    <option value="price_high_to_low">
                      Price: High to Low
                    </option>
                    <option value="name_a_to_z">Name: A to Z</option>
                    <option value="name_z_to_a">Name: Z to A</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Top End */}

            {/* Product List/Grid View */}
            <div className={`list-grid-wrapper ${grid ? "list-view" : ""}`}>
              {sortedProducts.length > 0 ? (
                sortedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2"
                  >
                    <Link
                      to={`/product-details/${product.id}`}
                      className="product-card__thumb flex-center rounded-8 bg-white position-relative w-full h-full p-0"
                    >
                      <img
                        src={
                          Array.isArray(product.image_fullpath)
                            ? product.image_fullpath[0]
                            : product.image_fullpath
                        }
                        alt={product.name}
                        className="w-full h-full object-cover rounded-8"
                      />
                    </Link>
                    <div className="product-card__content mt-16">
                      <h6 className="title text-lg fw-semibold mt-12 mb-8">
                        <Link
                          to={`/product-details/${product.id}`}
                          className="link text-line-2"
                          tabIndex={0}
                        >
                          {product.name}
                        </Link>
                      </h6>
                      <div className="mt-8"></div>
                      <div className="product-card__price my-20">
                        <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                          Rs.{product.price}
                        </span>
                        <span className="text-heading text-md fw-semibold">
                          Rs.
                          {product.price -
                            (product.price * product.discount) / 100}{" "}
                          <span className="text-gray-500 fw-normal">/Qty</span>{" "}
                        </span>
                      </div>
                      <button
                        to="#"
                        className="btn btn-main rounded-pill"
                        onClick={() => {
                          addToCart(product.id, 1);
                        }}
                      >
                        Add to Cart
                      </button>
                       
                    </div>
                  </div>
                ))
              ) : (
                <Empty description="No Product Found" />
              )}
            </div>
          </div>
          {/* Content End */}
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
